import React, { FC, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useDocumentBundles } from 'context/document-bundles-context';
import { BundleDocumentType, DocumentBundle } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { BUNDLE_DOCUMENT_TYPES, bundleDocumentTypeTranslationKey } from '../bundle-document-types';

type AddEditDocumentBundleDialogProps = {
    open: boolean;
    documentBundle?: DocumentBundle;
    onDialogClose: () => void;
};

// Map of selected document type -> number of copies. Presence of a key means the document is included.
type SelectedDocuments = Partial<Record<BundleDocumentType, number>>;

const AddEditDocumentBundleDialog: FC<AddEditDocumentBundleDialogProps> = ({ open, documentBundle, onDialogClose }) => {
    const { t: translate } = useTranslation(['lab-settings', 'orders', 'common']);
    const [title, setTitle] = useState('');
    const [selectedDocuments, setSelectedDocuments] = useState<SelectedDocuments>({});
    const { saveDocumentBundle, updateDocumentBundle } = useDocumentBundles();
    const { setAppToasterContent } = useContext(AppToasterContext);

    useEffect(() => {
        if (documentBundle?._id) {
            setTitle(documentBundle.title || '');
            const selected: SelectedDocuments = {};
            documentBundle.documents?.forEach((document) => {
                selected[document.documentType] = document.copies;
            });
            setSelectedDocuments(selected);
        } else {
            setTitle('');
            setSelectedDocuments({});
        }
    }, [documentBundle, open]);

    const handleToggleDocument = (documentType: BundleDocumentType, checked: boolean) => {
        setSelectedDocuments((previous) => {
            const next = { ...previous };
            if (checked) {
                next[documentType] = previous[documentType] ?? 1;
            } else {
                delete next[documentType];
            }
            return next;
        });
    };

    const handleCopiesChange = (documentType: BundleDocumentType, copies: number) => {
        setSelectedDocuments((previous) => ({
            ...previous,
            [documentType]: copies,
        }));
    };

    const isSubmitDisabled = !title.trim() || Object.keys(selectedDocuments).length === 0;

    const sortedDocumentTypes = [...BUNDLE_DOCUMENT_TYPES].sort((firstType, secondType) =>
        translate(bundleDocumentTypeTranslationKey(firstType)).localeCompare(
            translate(bundleDocumentTypeTranslationKey(secondType)),
        ),
    );

    const handleSubmit = async () => {
        const documentBundleData = {
            _id: documentBundle?._id,
            title: title.trim(),
            documents: BUNDLE_DOCUMENT_TYPES.filter((documentType) => selectedDocuments[documentType]).map(
                (documentType) => ({
                    documentType,
                    copies: selectedDocuments[documentType] ?? 1,
                }),
            ),
        };

        try {
            if (documentBundle?._id) {
                await updateDocumentBundle({ documentBundle: documentBundleData });
                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('common:CHANGES_SAVED'),
                });
            } else {
                await saveDocumentBundle({ documentBundle: documentBundleData });
                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('lab-settings:DOCUMENT_BUNDLE_ADDED_SUCCESSFULLY'),
                });
            }

            onDialogClose();
        } catch (error) {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('common:AN_ERROR_OCCURRED'),
            });
        }
    };

    return (
        <DHDialog
            title={
                documentBundle?._id
                    ? translate('lab-settings:EDIT_DOCUMENT_BUNDLE')
                    : translate('lab-settings:ADD_DOCUMENT_BUNDLE')
            }
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={onDialogClose}
            actions={
                <Button variant="contained" type="submit" onClick={handleSubmit} disabled={isSubmitDisabled}>
                    {documentBundle?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            }
        >
            <Box mt={1}>
                <TextField
                    fullWidth
                    label={translate('lab-settings:DOCUMENT_BUNDLE_TITLE')}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    variant="outlined"
                    size="small"
                    data-test="document-bundle-title-input"
                    required
                />

                <Grid container alignItems="center" spacing={1} mt={2} mb={1}>
                    <Grid item xs={9}>
                        <Typography variant="h4">{translate('lab-settings:DOCUMENTS')}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="h4">{translate('lab-settings:COPIES')}</Typography>
                    </Grid>
                </Grid>

                {sortedDocumentTypes.map((documentType) => {
                    const isChecked = selectedDocuments[documentType] !== undefined;
                    return (
                        <Grid container alignItems="center" spacing={1} key={documentType}>
                            <Grid item xs={9}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={(event) =>
                                                handleToggleDocument(documentType, event.target.checked)
                                            }
                                            data-test={`document-bundle-type-${documentType}`}
                                        />
                                    }
                                    label={translate(bundleDocumentTypeTranslationKey(documentType))}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    value={isChecked ? selectedDocuments[documentType] : ''}
                                    onChange={(event) =>
                                        handleCopiesChange(
                                            documentType,
                                            Math.max(1, parseInt(event.target.value, 10) || 1),
                                        )
                                    }
                                    variant="outlined"
                                    size="small"
                                    disabled={!isChecked}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        </Grid>
                    );
                })}
            </Box>
        </DHDialog>
    );
};

export default AddEditDocumentBundleDialog;
